/**
 * The reader's theme: light, dark, or the system's setting, which is the default.
 *
 * The choice is kept in this browser and shown as the `dark` class on the root element, which
 * every `dark:` style follows (styles.css). `public/theme.js` applies it before the page first
 * draws, so that a page never flashes in the other theme. This module applies changes: a new
 * choice, the system's setting changing while the choice is "system", and a choice made in
 * another tab.
 */

import { useEffect, useSyncExternalStore } from "react";

export type ThemeChoice = "light" | "dark" | "system";

/** Where the choice is kept. `public/theme.js` reads the same key. */
export const THEME_KEY = "morcast-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * The colour of the browser's own bars (`theme-color`) in each theme. It is the page's ground, so
 * that the bars run into the page. On the dark theme that is `--color-ink-950` in styles.css, and
 * public/theme.js sets the same colour before the app loads.
 */
const BROWSER_BARS = { light: "#ffffff", dark: "#0b0c0f" } as const;

/** The choice, when this browser refuses to store it (some private modes). */
let unstored: ThemeChoice | null = null;

const listeners = new Set<() => void>();

/** The reader's choice. "system" until they make one. */
export function readThemeChoice(): ThemeChoice {
  if (unstored) {
    return unstored;
  }
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

function systemIsDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

/** Shows the theme a choice stands for now. */
function applyTheme(choice: ThemeChoice): void {
  const dark = choice === "dark" || (choice === "system" && systemIsDark());
  const root = document.documentElement;
  root.classList.toggle("dark", dark);
  root.style.colorScheme = dark ? "dark" : "light";
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? BROWSER_BARS.dark : BROWSER_BARS.light);
}

/** Keeps a new choice, in this browser if it allows it, and shows it. */
export function setThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === "system") {
      localStorage.removeItem(THEME_KEY);
    } else {
      localStorage.setItem(THEME_KEY, choice);
    }
    unstored = null;
  } catch {
    unstored = choice;
  }
  applyTheme(choice);
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The reader's choice, kept current as it changes. */
export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(subscribe, readThemeChoice, () => "system");
}

/**
 * Keeps the page in the reader's theme. It follows the system's setting while the choice is
 * "system", and choices made in other tabs. Used once, by the page frame.
 */
export function useThemeSync(): void {
  const choice = useThemeChoice();

  useEffect(() => {
    applyTheme(choice);
    if (choice !== "system" || typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia(DARK_QUERY);
    const follow = () => applyTheme("system");
    query.addEventListener("change", follow);
    return () => query.removeEventListener("change", follow);
  }, [choice]);

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === THEME_KEY || event.key === null) {
        for (const listener of listeners) {
          listener();
        }
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
}
